import React, { useState, useMemo, useCallback, useEffect } from "react";
import { exportTaktPdf, fetchTaktExportTitle } from "../../../api/clientV2";
import type { Trade } from "../../../models";
import { computeEndDate } from "./helpers";
import "./ExportTaktModal.css";

interface Props {
  scheduleId: number;
  trades: Trade[];
  onClose: () => void;
}

type DisplayType = "detailed" | "overview";
type Scope = "all" | "by_trade";
type PageSizeKey = "11x17" | "18x24" | "24x36" | "30x42" | "36x48" | "custom";

const UNASSIGNED_ID = -1;

const OVERVIEW_MAX_WEEKS = 26;
const DETAILED_MAX_WEEKS = 8;

const PAGE_SIZE_OPTIONS: { key: PageSizeKey; label: string }[] = [
  { key: "11x17", label: "11 × 17 (Tabloid)" },
  { key: "18x24", label: "18 × 24 (ARCH C)" },
  { key: "24x36", label: "24 × 36 (ARCH D)" },
  { key: "30x42", label: "30 × 42 (ARCH E1)" },
  { key: "36x48", label: "36 × 48 (ARCH E)" },
  { key: "custom", label: "Custom…" },
];

const ExportTaktModal: React.FC<Props> = ({ scheduleId, trades, onClose }) => {
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [displayType, setDisplayType] = useState<DisplayType>("overview");
  const [pageSize, setPageSize] = useState<PageSizeKey>("11x17");
  const [customW, setCustomW] = useState("36");
  const [customH, setCustomH] = useState("24");
  const [startDate, setStartDate] = useState(todayIso);
  const [numWeeks, setNumWeeks] = useState(6);
  const [showNames, setShowNames] = useState(false);
  // Title: seeded with the export's default (project / schedule name), then
  // freely editable. Until the default arrives the field shows a placeholder.
  const [title, setTitle] = useState("");
  const [titleLoaded, setTitleLoaded] = useState(false);
  const [scope, setScope] = useState<Scope>("all");
  const [selectedTradeIds, setSelectedTradeIds] = useState<Set<number>>(
    () => new Set([...trades.map((t) => t.id), UNASSIGNED_ID])
  );
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchTaktExportTitle(scheduleId)
      .then((t) => {
        if (!cancelled) setTitle(t);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setTitleLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [scheduleId]);

  const endDate = useMemo(
    () => computeEndDate(startDate, numWeeks),
    [startDate, numWeeks]
  );

  const approxWorkdays = numWeeks * 5;
  const maxWeeks = displayType === "overview" ? OVERVIEW_MAX_WEEKS : DETAILED_MAX_WEEKS;
  const weekOptions = useMemo(
    () => Array.from({ length: maxWeeks }, (_, i) => i + 1),
    [maxWeeks]
  );

  const showDimensions = displayType === "overview";
  const customDimsInvalid =
    showDimensions &&
    pageSize === "custom" &&
    (!(Number(customW) > 0) || !(Number(customH) > 0));

  const changeDisplayType = useCallback((next: DisplayType) => {
    setDisplayType(next);
    const cap = next === "overview" ? OVERVIEW_MAX_WEEKS : DETAILED_MAX_WEEKS;
    setNumWeeks((w) => Math.min(w, cap));
  }, []);

  // All items = all trades + unassigned
  const allItemIds = useMemo(
    () => new Set([...trades.map((t) => t.id), UNASSIGNED_ID]),
    [trades]
  );
  const selectAll = useCallback(() => {
    setSelectedTradeIds(new Set(allItemIds));
  }, [allItemIds]);

  const clearAll = useCallback(() => {
    setSelectedTradeIds(new Set());
  }, []);

  const toggleTrade = useCallback((id: number) => {
    setSelectedTradeIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Build trade names for PDF subtitle
  const selectedTradeNames = useMemo(() => {
    if (scope === "all") return [];
    const names: string[] = [];
    for (const t of trades) {
      if (selectedTradeIds.has(t.id)) names.push(t.name);
    }
    if (selectedTradeIds.has(UNASSIGNED_ID)) names.push("Unassigned");
    return names;
  }, [scope, trades, selectedTradeIds]);

  const handleExport = async () => {
    setExporting(true);
    setError(null);
    try {
      let tradeIds: number[] = [];
      let tradeNames: string[] = [];
      let includeUnassigned = true;

      if (scope === "by_trade") {
        tradeIds = Array.from(selectedTradeIds).filter((id) => id !== UNASSIGNED_ID);
        tradeNames = selectedTradeNames;
        includeUnassigned = selectedTradeIds.has(UNASSIGNED_ID);
      }

      const effectivePageSize = showDimensions ? pageSize : "11x17";
      await exportTaktPdf(
        scheduleId, startDate, numWeeks, displayType, tradeIds, tradeNames, includeUnassigned,
        effectivePageSize,
        effectivePageSize === "custom" ? Number(customW) : undefined,
        effectivePageSize === "custom" ? Number(customH) : undefined,
        showNames,
        title.trim() || undefined,
      );
      onClose();
    } catch (err: any) {
      const msg =
        err?.response?.data instanceof Blob
          ? await err.response.data.text().then((t: string) => {
              try { return JSON.parse(t).error; } catch { return t; }
            })
          : err?.response?.data?.error || "Export failed. Please try again.";
      setError(msg);
    } finally {
      setExporting(false);
    }
  };

  const byTradeDisabled = scope === "by_trade" && selectedTradeIds.size === 0;
  const exportDisabled = exporting || !startDate || byTradeDisabled || customDimsInvalid;

  return (
    <div className="export-takt-overlay" onClick={onClose}>
      <div className="export-takt-modal" onClick={(e) => e.stopPropagation()}>
        <div className="export-takt-title">Export Lookahead</div>

        <div className="export-takt-field">
          <label className="export-takt-label">Title</label>
          <input
            type="text"
            className="export-takt-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={titleLoaded ? "Title for the export" : "Loading…"}
          />
        </div>

        <div className="export-takt-field">
          <label className="export-takt-label">Display</label>
          <select
            className="export-takt-select"
            value={displayType}
            onChange={(e) => changeDisplayType(e.target.value as DisplayType)}
          >
            <option value="overview">Single page</option>
            <option value="detailed">Detailed (Multi-page)</option>
          </select>
        </div>

        {showDimensions && (
          <div className="export-takt-field">
            <label className="export-takt-label">Dimensions</label>
            <select
              className="export-takt-select"
              value={pageSize}
              onChange={(e) => setPageSize(e.target.value as PageSizeKey)}
            >
              {PAGE_SIZE_OPTIONS.map((opt) => (
                <option key={opt.key} value={opt.key}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {showDimensions && pageSize === "custom" && (
          <div className="export-takt-field">
            <label className="export-takt-label">Size (in)</label>
            <div className="export-takt-custom-dims">
              <input
                type="number"
                min={8}
                max={200}
                className="export-takt-input"
                aria-label="Custom width in inches"
                value={customW}
                onChange={(e) => setCustomW(e.target.value)}
              />
              <span className="export-takt-custom-x">×</span>
              <input
                type="number"
                min={8}
                max={200}
                className="export-takt-input"
                aria-label="Custom height in inches"
                value={customH}
                onChange={(e) => setCustomH(e.target.value)}
              />
            </div>
          </div>
        )}

        <div className="export-takt-field">
          <label className="export-takt-label">Start Date</label>
          <input
            type="date"
            className="export-takt-input"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div className="export-takt-field">
          <label className="export-takt-label">Weeks</label>
          <select
            className="export-takt-select"
            value={numWeeks}
            onChange={(e) => setNumWeeks(Number(e.target.value))}
          >
            {weekOptions.map((n) => (
              <option key={n} value={n}>
                {n} week{n > 1 ? "s" : ""}
              </option>
            ))}
          </select>
        </div>

        <label className="export-takt-check">
          <input
            type="checkbox"
            checked={showNames}
            onChange={(e) => setShowNames(e.target.checked)}
          />
          Show work-code names on bars (falls back to the code when they don&rsquo;t fit)
        </label>

        {trades.length > 0 && (
          <div className="export-takt-scope">
            <label className="export-takt-label">Scope</label>
            <div className="export-takt-scope-options">
              <label className="export-takt-radio">
                <input
                  type="radio"
                  name="scope"
                  checked={scope === "all"}
                  onChange={() => setScope("all")}
                />
                All Activities
              </label>
              <label className="export-takt-radio">
                <input
                  type="radio"
                  name="scope"
                  checked={scope === "by_trade"}
                  onChange={() => setScope("by_trade")}
                />
                By Trade
              </label>
            </div>
          </div>
        )}

        {scope === "by_trade" && trades.length > 0 && (
          <div className="export-takt-trades">
            <div className="export-takt-trades-header">
              <button className="export-takt-toggle-all" onClick={selectAll}>Select All</button>
              <button className="export-takt-toggle-all" onClick={clearAll}>Clear All</button>
            </div>
            <div className="export-takt-trades-list">
              {trades.map((trade) => (
                <label key={trade.id} className="export-takt-trade-item">
                  <input
                    type="checkbox"
                    checked={selectedTradeIds.has(trade.id)}
                    onChange={() => toggleTrade(trade.id)}
                  />
                  <span
                    className="export-takt-trade-swatch"
                    style={{ background: trade.fill_color }}
                  />
                  <span className="export-takt-trade-name">{trade.name}</span>
                </label>
              ))}
              <label className="export-takt-trade-item">
                <input
                  type="checkbox"
                  checked={selectedTradeIds.has(UNASSIGNED_ID)}
                  onChange={() => toggleTrade(UNASSIGNED_ID)}
                />
                <span
                  className="export-takt-trade-swatch"
                  style={{ background: "#555555" }}
                />
                <span className="export-takt-trade-name">Unassigned</span>
              </label>
            </div>
          </div>
        )}

        <div className="export-takt-summary">
          {startDate} &mdash; {endDate} (~{approxWorkdays} workdays)
          {scope === "by_trade" && selectedTradeNames.length > 0 && (
            <span> &middot; {selectedTradeNames.join(", ")}</span>
          )}
        </div>

        {error && <div className="export-takt-error">{error}</div>}

        <div className="export-takt-actions">
          <button className="export-takt-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            className="export-takt-submit"
            onClick={handleExport}
            disabled={exportDisabled}
          >
            {exporting ? "Exporting..." : "Export PDF"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportTaktModal;
